import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {CheckCircle2, Mailbox, Trophy, Users, Zap} from "lucide-react";
import {CLIENT_SCHEMA, FAQ_SCHEMA, FIXER_SCHEMA, SEO_CONFIG} from "../lib/seo";
import {trackCTAClick, trackModal, trackWaitlistSignup} from "../lib/analytics";
import CookieConsentBanner from "./CookieConsentBanner";

function generateRandomNumber() {
    return Math.floor(Math.random() * (2000 - 1000 + 1)) + 1000;
}

const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const [modalEmail, setModalEmail] = useState("");
  const [modalSubmitted, setModalSubmitted] = useState(false);
  const [selectedTier, setSelectedTier] = useState(2); // Default to tier 2 (Professional)
  const [viewMode, setViewMode] = useState('client');
  const [fixersCount, setFixersCount] = useState(generateRandomNumber());
  const [clientsCount, setClientsCount] = useState(generateRandomNumber());

  const logos = ['/blue.svg', '/green.svg', '/red.svg'];

  // Get current SEO config based on view mode
  const currentSEO = SEO_CONFIG[viewMode];
  const currentSchema = viewMode === 'client' ? CLIENT_SCHEMA : FIXER_SCHEMA;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogo((prev) => (prev + 1) % logos.length);
    }, 2000); // Change logo every 2 seconds

    return () => clearInterval(interval);
  }, []);

  // Scroll detection for modal
  useEffect(() => {
    const handleScroll = () => {
      if (modalShown) return; // Don't show again if already shown

      const testimonialsSection = document.querySelector('#testimonials');
      if (testimonialsSection) {
        const rect = testimonialsSection.getBoundingClientRect();
        const hasScrolledPast = rect.bottom < window.innerHeight;

        if (hasScrolledPast) {
          setShowModal(true);
          setModalShown(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [modalShown]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
        trackModal('close');
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('/api/submit-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    viewMode,
                    source: 'main_form'
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Κάτι πήγε στραβά');
            }

            // Track analytics
            trackWaitlistSignup(email, viewMode, 'main');

            setSubmitted(true);
            setTimeout(() => {
                setEmail("");
                setSubmitted(false);
            }, 3000);

        } catch (error) {
            console.error('Error:', error);
            alert('Σφάλμα κατά την αποθήκευση. Δοκιμάστε ξανά.');
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('/api/submit-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: modalEmail,
                    viewMode,
                    source: 'modal'
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Κάτι πήγε στραβά');
            }

            // Track analytics
            trackWaitlistSignup(modalEmail, viewMode, 'modal');

            setModalSubmitted(true);
            setTimeout(() => {
                setShowModal(false);
                setModalEmail("");
                setModalSubmitted(false);
            }, 2000);

        } catch (error) {
            console.error('Error:', error);
            alert('Σφάλμα κατά την αποθήκευση. Δοκιμάστε ξανά.');
        }
    };

  return (
    <div className="min-h-screen bg-background">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{currentSEO.title}</title>
        <meta name="title" content={currentSEO.title} />
        <meta name="description" content={currentSEO.description} />
        <meta name="keywords" content={currentSEO.keywords} />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content={currentSEO.ogTitle} />
        <meta property="og:description" content={currentSEO.ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://volepse.me/" />
        <meta property="og:image" content="https://volepse.me/android-chrome-512x512.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/png" />

        {/* Twitter */}
        <meta property="twitter:title" content={currentSEO.ogTitle} />
        <meta property="twitter:description" content={currentSEO.ogDescription} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:image" content="https://volepse.me/android-chrome-512x512.png" />
        <meta property="twitter:image:alt" content="Volepse.me - Το Μεγαλύτερο Δίκτυο Βυσμάτων στην Ελλάδα" />

        {/* Structured Data - Dynamic based on view */}
        <script type="application/ld+json">
          {JSON.stringify(currentSchema)}
        </script>

        {/* FAQ Schema - Always include for better SEO */}
        <script type="application/ld+json">
          {JSON.stringify(FAQ_SCHEMA)}
        </script>
      </Helmet>

      {/* Fixed Header with View Toggle */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-center">
          <div className="inline-flex items-center bg-muted rounded-full p-1 shadow-lg">
            <button
              onClick={() => setViewMode('client')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                viewMode === 'client'
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Θέλω Βύσμα
            </button>
            <button
              onClick={() => setViewMode('fixer')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                viewMode === 'fixer'
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Είμαι Βύσμα
            </button>
          </div>
        </div>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {viewMode === 'client' ? (
          // CLIENT VIEW - Current Landing Page
          <>
      {/* Section 1: Hero with Logo */}
      <section className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
        <div className="text-center max-w-4xl mx-auto">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <div className="w-32 h-32 relative">
              {logos.map((logo, index) => (
                <img
                  key={logo}
                  src={logo}
                  alt="Volepse Me Logo"
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                    index === currentLogo ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-primary bg-clip-text text-transparent">
            Θέλω Βύσμα
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Ήρθες επιτέλους η ώρα να βολέψει κάποιος και ΕΣΕΝΑ. Εχεις τώρα και εσυ το βύσμα που χρειάζεσαι εύκολα και με ασφάλεια.
          </p>

          <button
            onClick={() => {
              trackCTAClick('Hero CTA - Client View', 'waitlist');
              document
                .getElementById("waitlist")
                .scrollIntoView({ behavior: "smooth" });
            }}
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
          >
            ΒΟΛΕΨΕ ΜΕ
          </button>
        </div>
      </section>

      {/* Section 2: How It Works */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
            Πώς Δουλεύει το Βύσμα
          </h2>
          <p className="text-center text-muted-foreground mb-16 text-lg">
            3 βήματα και τελείωσες! Όχι άλλα ταλαιπωρία και χαμένες ώρες
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="group h-[300px] [perspective:1000px]">
              <div className="relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                {/* Front */}
                <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                  <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                      <span className="text-3xl font-bold text-primary">1</span>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4">
                      Πες Μας Τι Σε Πρήζει
                    </h3>
                    <p className="text-muted-foreground">
                      Στείλε μας το πρόβλημά σου. Άδειες που κολλάνε; Χαρτιά που
                      θέλουν υπογραφή από 20 υπηρεσίες; Χρειάζεσαι γνωριμία; Εμείς
                      έχουμε τα ΠΑΝΤΑ!
                    </p>
                  </div>
                </div>
                {/* Back */}
                <div className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                  <div className="bg-gradient-to-br from-primary to-purple-600 rounded-xl p-8 h-full flex flex-col justify-center text-white">
                    <div className="text-center">
                      <div className="text-5xl mb-4">📝</div>
                      <h4 className="text-xl font-bold mb-3">Τι Μπορούμε να Κάνουμε;</h4>
                      <ul className="text-sm space-y-2 text-left">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Άδειες οικοδομών</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Επιδοτήσεις & χρηματοδοτήσεις</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Ραντεβού σε γιατρούς</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Προσλήψεις στο δημόσιο</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="group h-[300px] [perspective:1000px]">
              <div className="relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                {/* Front */}
                <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                  <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                      <span className="text-3xl font-bold text-primary">2</span>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4">
                      Ενεργοποιούμε το Δίκτυο
                    </h3>
                    <p className="text-muted-foreground">
                      Εκατοντάδες άνθρωποι σε θέσεις ισχύος σε δημόσιο, τράπεζες,
                      παντού! Σε 24 ώρες βρίσκουμε ποιος σε φτιάχνει. Όχι μήνες
                      αναμονής, μην είσαι κορόιδο!
                    </p>
                  </div>
                </div>
                {/* Back */}
                <div className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                  <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-8 h-full flex flex-col justify-center text-white">
                    <div className="text-center">
                      <div className="text-5xl mb-4">🔗</div>
                      <h4 className="text-xl font-bold mb-3">Το Δίκτυό Μας</h4>
                      <ul className="text-sm space-y-2 text-left">
                        <li className="flex items-start gap-2">
                          <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>5,000+ ενεργά βύσματα</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Σε όλα τα υπουργεία</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Τράπεζες & ασφάλειες</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Νοσοκομεία & κλινικές</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="group h-[300px] [perspective:1000px]">
              <div className="relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                {/* Front */}
                <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                  <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                      <span className="text-3xl font-bold text-primary">3</span>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4">
                      Βολεύτηκες!
                    </h3>
                    <p className="text-muted-foreground">
                      Σε 2-3 μέρες είσαι έτοιμος! Η άδεια βγήκε, το χαρτί υπογράφτηκε,
                      το ραντεβού κλείστηκε. Πλήρωνεις εμάς και εμείς τα κάνουμε δωρεά εκει που πρέπει
                        για να συνεχίσουμε να βολεύουμε κόσμο!
                    </p>
                  </div>
                </div>
                {/* Back */}
                <div className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-8 h-full flex flex-col justify-center text-white">
                    <div className="text-center">
                      <div className="text-5xl mb-4">
                          🏆
                      </div>
                      <h4 className="text-xl font-bold mb-3">Τι Κερδίζεις;</h4>
                      <ul className="text-sm space-y-2 text-left">
                        <li className="flex items-start gap-2">
                          <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Γλυτώνεις 6+ μήνες αναμονής</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Όχι άγχος & ταλαιπωρία</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>100% διακριτικότητα</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Αποτελέσματα εγγυημένα</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Section 3: Who We Work With */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
            Τα Βύσματά Μας
          </h2>
          <p className="text-center text-muted-foreground mb-16 text-lg">
            Εκατοντάδες άνθρωποι στο δίκτυο μας παντού στην Ελλάδα! Από υπουργεία, τράπεζες, στρατόπεδα, νοσοκομεία μέχρι και σουβλατζίδικα - ΠΑΝΤΟΥ έχουμε ανθρώπους να βοηθήσουν!
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Profile 1 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mb-4 flex items-center justify-center overflow-hidden">
                  <img
                    src="https://api.dicebear.com/7.x/avataaars/svg?seed=Frapedakias"
                    alt="Emma Thompson"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  Dimitris Papadopoulos
                </h3>
                <p className="text-sm text-primary font-medium mb-3">
                  Ρουσφετάκιας στο Δημόσιο
                </p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Άδειες σε 48 ώρες</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Παράθυρα που δουλεύουν</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Χαρτιά χωρίς αγγαρεία</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Profile 2 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4 flex items-center justify-center overflow-hidden">
                  <img
                    src="https://api.dicebear.com/7.x/avataaars/svg?seed=MariaK"
                    alt="Marcus Chen"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  Maria Konstantinou
                </h3>
                <p className="text-sm text-primary font-medium mb-3">
                  Κονέ σε Μεγάλες Εταιρείες
                </p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Συστάσεις με CEO</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Deals που βγαίνουν</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Σύλλογοι & Lobbyists</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Profile 3 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mb-4 flex items-center justify-center overflow-hidden">
                  <img
                    src="https://api.dicebear.com/7.x/avataaars/svg?seed=xoriataros"
                    alt="Sofia Rodriguez"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  Nikos Georgiou
                </h3>
                <p className="text-sm text-primary font-medium mb-3">
                  Μαφιόζος Ακινήτων
                </p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Σπίτια off-market</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Μεσίτες που κόβουν</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Οικόπεδα με μίζες</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Profile 4 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mb-4 flex items-center justify-center overflow-hidden">
                  <img
                    src="https://api.dicebear.com/7.x/avataaars/svg?seed=EleniC"
                    alt="David Okonkwo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  Eleni Vasilaki
                </h3>
                <p className="text-sm text-primary font-medium mb-3">
                  Βύσμα στα Νοσοκομεία
                </p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Ραντεβού σε 2 μέρες</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Γιατροί χωρίς ουρές</span>
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Εξετάσεις express</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Pricing */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
            Πόσο Κοστίζει το Βύσμα;
          </h2>
          <p className="text-center text-muted-foreground mb-16 text-lg">
            Διάλεξε το πακέτο που σου ταιριάζει - Όλα με απόλυτη διακριτικότητα!
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Tier 1: Small */}
            <div
              onClick={() => setSelectedTier(1)}
              className={`bg-card rounded-2xl p-8 transition-all cursor-pointer ${
                selectedTier === 1 
                  ? 'border-2 border-primary shadow-xl scale-105' 
                  : 'border border-border hover:shadow-lg hover:-translate-y-1'
              }`}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">Μικρό Βύσμα</h3>
                <p className="text-sm text-muted-foreground mb-4">Για απλές δουλειές</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-foreground">€25</span>
                  <span className="text-muted-foreground">/μήνα</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">1-2 μικροδουλειές τον μήνα</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Απλά χαρτιά & άδειες</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Υποστήριξη με email</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Βασικό δίκτυο μέσων</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Απάντηση σε 48 ώρες</span>
                </li>
              </ul>

              <button
                onClick={() =>
                  document
                    .getElementById("waitlist")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="w-full px-6 py-3 bg-muted text-foreground border border-border rounded-lg font-semibold hover:bg-muted/80 transition-colors"
              >
                Ξεκίνα Τώρα
              </button>
            </div>

            {/* Tier 2: Professional (Popular) */}
            <div
              onClick={() => setSelectedTier(2)}
              className={`bg-card rounded-2xl p-8 shadow-xl relative cursor-pointer transition-all ${
                selectedTier === 2
                  ? 'border-2 border-primary scale-105'
                  : 'border-2 border-border hover:scale-105'
              }`}
            >
              {selectedTier === 2 && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
                    🔥 ΤΟ ΚΑΛΥΤΕΡΟ
                  </span>
                </div>
              )}

              <div className="text-center mb-6 mt-2">
                <h3 className="text-2xl font-bold text-foreground mb-2">Σοβαρό Βύσμα</h3>
                <p className="text-sm text-muted-foreground mb-4">Για σοβαρές δουλειές</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-primary">€100</span>
                  <span className="text-muted-foreground">/μήνα</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">5-10 σοβαρές δουλειές τον μήνα</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">Σοβαρές & πολύπλοκες υποθέσεις</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">Προτεραιότητα στην υποστήριξη</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">Πρόσβαση σε TOP μέσα</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">Τηλεφωνική υποστήριξη 24/7</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">Απάντηση σε 3 ώρες</span>
                </li>
              </ul>

              <button
                onClick={() =>
                  document
                    .getElementById("waitlist")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
              >
                Βόλεψέ Με ΤΩΡΑ!
              </button>
            </div>

            {/* Tier 3: Enterprise */}
            <div
              onClick={() => setSelectedTier(3)}
              className={`bg-card rounded-2xl p-8 transition-all cursor-pointer ${
                selectedTier === 3
                  ? 'border-2 border-primary shadow-xl scale-105'
                  : 'border border-border hover:shadow-lg hover:-translate-y-1'
              }`}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">VIP Βύσμα</h3>
                <p className="text-sm text-muted-foreground mb-4">Για τους... ειδικούς</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-foreground">€500</span>
                  <span className="text-muted-foreground">/μήνα</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Απεριόριστες συνδέσεις</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Λύνουμε ΤΑ ΠΑΝΤΑ</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Αποκλειστικός account manager</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Πρόσβαση σε ΟΛΟ το δίκτυο</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Επιχειρηματικές συνδέσεις</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Άμεση απάντηση (30 λεπτά)</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">100% εγγύηση επιτυχίας</span>
                </li>
              </ul>

              <button
                onClick={() =>
                  document
                    .getElementById("waitlist")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="w-full px-6 py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-lg font-semibold hover:from-primary/90 hover:to-purple-600/90 transition-colors shadow-lg"
              >
                Γίνε VIP Μέλος
              </button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">
              💳 Όλες οι τιμές + ΦΠΑ • Ακύρωση όποτε θες • Πλήρης διακριτικότητα εγγυημένη
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Testimonials */}
      <section id="testimonials" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
            Δες Πόσους Βολέψαμε Ήδη!
          </h2>
          <p className="text-center text-muted-foreground mb-16 text-lg">
            Χιλιάδες άνθρωποι που γλίτωσαν από τη γραφειοκρατία με τα βύσματά
            μας!
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  AP
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-foreground">Η Μανάβισσα</h4>
                  <p className="text-sm text-muted-foreground">Μικρομεσαία</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-2">★★★★★</div>
              <p className="text-muted-foreground">
                "2 χρόνια περίμενα για την άδεια, ενω οι άλλοι προχωρούσαν! Με το "Βόλεψε Με" την έβγαλα μεσα σε 2 μέρες. Ουτε γνωρίζω ποιος το προχώρησε, ούτε με νοιάζει. Αξίζει κάθε ευρώ ρε παιδιά!"
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  KM
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-foreground">Κύριος Καπουτσίνος</h4>
                  <p className="text-sm text-muted-foreground">Freelancer</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-2">★★★★★</div>
              <p className="text-muted-foreground">
                "Ήξερα άλλους που έπαιρναν παράνομες επιδοτήσεις για να ζούνε και δεν το πίστευα. Με το "Βόλεψε Με" κατάφερα να πάρω κι εγώ, χωρίς να ρισκάρω τίποτα. Η Ferrari στο garage τα λέει όλα, να είναι καλά η μανούλα μου!"
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  GT
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-foreground">Κύριος Αυγολέμονος</h4>
                  <p className="text-sm text-muted-foreground">Βιοτέχνης</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-2">★★★★★</div>
              <p className="text-muted-foreground">
                Χωρίς μέσον δεν γίνεται τίποτα σε αυτή τη χώρα! Το "Βόλεψε Με" με
                έσωσε, με σύστησαν σε μεγάλα ονόματα και τριπλασίασα τον τζίρο μου.
                ΟΛΟΙ πρέπει να το ξέρουν!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Scandals - Don't End Up Like Them */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
            Μην Καταλήξεις Σαν Αυτούς
          </h2>
          <p className="text-center text-muted-foreground mb-16 text-lg">
            Δεν είχαν το σωστό μέσον... Εσύ όμως μπορείς να το έχεις!
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Scandal 1: ΟΠΕΚΕΠΕ */}
            <div className="bg-card border-2 border-red-500/20 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center text-white text-2xl mb-4">
                  ⚠️
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">ΟΠΕΚΕΠΕ</h3>
                <p className="text-sm text-red-500 font-semibold mb-4">
                  Δεκάδες έρευνες για Εκατομμύρια ευρώ
                </p>
              </div>
              <p className="text-muted-foreground mb-4">
                Πήγαν να πάρουν επιδοτήσεις χωρίς το σωστό κονέ. <br/>Στο δια τάυτα:
                Συλλήψεις, δικαστήρια, και χαμένα εκατομμύρια. Αν είχαν μέσον που ήξερε τι έπρεπε να γίνει ακόμα θα τρώγανε.
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-red-500 font-semibold">
                  ❌ Χωρίς εμάς φλερτάρεις με τη φυλακή!
                </p>
              </div>
            </div>

            {/* Scandal 2: Novartis */}
            <div className="bg-card border-2 border-red-500/20 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center text-white text-2xl mb-4">
                  ⚠️
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Novartis</h3>
                <p className="text-sm text-red-500 font-semibold mb-4">
                  Πολιτικό σκάνδαλο • Διεθνής ντροπή
                </p>
              </div>
              <p className="text-muted-foreground mb-4">
                Νόμιζαν ότι τα βύσματα που είχαν ήταν αρκετά. Λάθος! Χωρίς διακριτικότητα και το σωστό δίκτυο, όλα βγήκαν στη φόρα. Ξέρεις ποιος δεν έπαθε τίποτα; Οι δικοί μας!
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-red-500 font-semibold">
                  ❌ Λάθος βύσμα = Σκάνδαλο
                </p>
              </div>
            </div>

            {/* Scandal 3: Siemens */}
            <div className="bg-card border-2 border-red-500/20 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center text-white text-2xl mb-4">
                  ⚠️
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Siemens</h3>
                <p className="text-sm text-red-500 font-semibold mb-4">
                  Δωροδοκία • Δισεκατομμύρια κόστος
                </p>
              </div>
              <p className="text-muted-foreground mb-4">
                Το μεγαλύτερο σκάνδαλο! Χρηματισμοί χωρίς σχέδιο και χωρίς το
                σωστό δίκτυο. Με το "Βόλεψε Με", τα πράγματα θα ήταν ΕΝΤΕΛΩΣ διαφορετικά...
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-red-500 font-semibold">
                  ❌ Αδέξιο βύσμα = Καταστροφή
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Section 7: Waitlist Signup */}
      <section id="waitlist" className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Μπες ΤΩΡΑ στη Λίστα!
          </h2>
          <p className="text-muted-foreground mb-8 text-lg">
            Γίνε από τους πρώτους που θα έχουν πρόσβαση σε ΧΙΛΙΑΔΕΣ βύσματα!
            Ειδική τιμή μόνο για τους πρώτους 1000!
          </p>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Βάλε το email σου εδώ"
                required
                className="flex-1 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                disabled={submitted}
                className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitted ? "✓ Μέσα!" : "ΘΕΛΩ ΒΥΣΜΑ!"}
              </button>
            </div>
          </form>

          {submitted && (
            <p className="mt-4 text-green-600 dark:text-green-400 font-medium">
              ΜΠΡΑΒΟ! Θα σε καλέσουμε σύντομα!
            </p>
          )}

          <p className="mt-6 text-sm text-muted-foreground">
            Ήδη <span className="font-semibold text-foreground">{clientsCount}</span>{" "}
            άτομα περιμένουν να φτιαχτούν!
          </p>

          <div className="mt-8 flex justify-center gap-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              Καμία ενόχληση
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              Απόλυτη διακριτικότητα
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-6xl mx-auto text-center text-muted-foreground text-sm">
          <p>&copy; 2025 Volepse.me. Με επιφύλαξη παντός δικαιώματος.</p>
        </div>
      </footer>
          </>
        ) : (
          // FIXER VIEW - Landing Page for Fixers
          <>
              {/* Section 1: Hero with Logo for Fixers */}
              <section className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
                  <div className="text-center max-w-4xl mx-auto">
                      {/* Logo */}
                      <div className="mb-8 flex justify-center">
                          <div className="w-32 h-32 relative">
                              {logos.map((logo, index) => (
                                  <img
                                      key={logo}
                                      src={logo}
                                      alt="Volepse Me Logo"
                                      className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                                          index === currentLogo ? 'opacity-100' : 'opacity-0'
                                      }`}
                                  />
                              ))}
                          </div>
                      </div>

                      <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-primary bg-clip-text text-transparent">
                          Είμαι Βύσμα
                      </h1>

                      <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                          Γίνε μέλος του μεγαλύτερου δικτύου βυσμάτων στην Ελλάδα και βγάλε χοντρά λεφτά βοηθώντας ανθρώπους να λύσουν τα προβλήματά τους!
                      </p>

                      <button
                          onClick={() =>
                              document
                                  .getElementById("waitlist")
                                  .scrollIntoView({behavior: "smooth"})
                          }
                          className="px-8 py-4 bg-primary text-primary-foreground rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
                      >
                          ΒΟΛΕΨΕ ΤΟΥΣ
                      </button>
                  </div>
              </section>

              {/* Section 2: How It Works for Fixers */}
              <section className="py-20 px-4 bg-muted/30">
                  <div className="max-w-6xl mx-auto">
                      <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
                          Πώς Λειτουργεί για Εσένα
                      </h2>
                      <p className="text-center text-muted-foreground mb-16 text-lg">
                          3 απλά βήματα για να αρχίσεις να βγάζεις λεφτά!
                      </p>

                      <div className="grid md:grid-cols-3 gap-8">
                          {/* Step 1 */}
                          <div className="group h-[300px] [perspective:1000px]">
                              <div
                                  className="relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                                  {/* Front */}
                                  <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                                      <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col">
                                          <div
                                              className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                                              <span className="text-3xl font-bold text-primary">1</span>
                                          </div>
                                          <h3 className="text-2xl font-semibold text-foreground mb-4">
                                              Εγγραφή & Επαλήθευση
                                          </h3>
                                          <p className="text-muted-foreground">
                                              Κάνε εγγραφή και πες μας ποιες γνωριμίες έχεις. Τι μπορείς
                                              να λύσεις; Σε ποιο χώρο; Εμείς επαληθεύουμε το προφίλ σου
                                              και σε συνδέουμε με τους πελάτες!
                                          </p>
                                      </div>
                                  </div>
                                  {/* Back */}
                                  <div
                                      className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                                      <div
                                          className="bg-gradient-to-br from-primary to-purple-600 rounded-xl p-8 h-full flex flex-col justify-center text-white">
                                          <div className="text-center">
                                              <div className="text-5xl mb-4">✅</div>
                                              <h4 className="text-xl font-bold mb-3">Τι Χρειάζεσαι;</h4>
                                              <ul className="text-sm space-y-2 text-left">
                                                  <li className="flex items-start gap-2">
                                                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Αποδείξιμες γνωριμίες</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Διακριτικότητα 100%</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Επαγγελματισμό</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Αποτελεσματικότητα</span>
                                                  </li>
                                              </ul>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          </div>

                          {/* Step 2 */}
                          <div className="group h-[300px] [perspective:1000px]">
                              <div
                                  className="relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                                  {/* Front */}
                                  <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                                      <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col">
                                          <div
                                              className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                                              <span className="text-3xl font-bold text-primary">2</span>
                                          </div>
                                          <h3 className="text-2xl font-semibold text-foreground mb-4">
                                              Δέξου Αιτήματα
                                          </h3>
                                          <p className="text-muted-foreground">
                                              Θα σου στέλνουμε πελάτες που χρειάζονται τη βοήθειά σου!
                                              Εσύ διαλέγεις ποια αιτήματα θες να αναλάβεις. Όχι πίεση,
                                              δουλεύεις με το ρυθμό σου!
                                          </p>
                                      </div>
                                  </div>
                                  {/* Back */}
                                  <div
                                      className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                                      <div
                                          className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-8 h-full flex flex-col justify-center text-white">
                                          <div className="text-center">
                                              <div className="text-5xl mb-4">📱</div>
                                              <h4 className="text-xl font-bold mb-3">Τα Πλεονεκτήματα</h4>
                                              <ul className="text-sm space-y-2 text-left">
                                                  <li className="flex items-start gap-2">
                                                      <Users className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Επιλέγεις εσύ τα αιτήματα</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <Users className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Καθορίζεις το ωράριό σου</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <Users className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Προστασία από το Volepse.me</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <Users className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Άμεση επικοινωνία με πελάτες</span>
                                                  </li>
                                              </ul>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          </div>

                          {/* Step 3 */}
                          <div className="group h-[300px] [perspective:1000px]">
                              <div
                                  className="relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                                  {/* Front */}
                                  <div className="absolute inset-0 h-full w-full [backface-visibility:hidden]">
                                      <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col">
                                          <div
                                              className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                                              <span className="text-3xl font-bold text-primary">3</span>
                                          </div>
                                          <h3 className="text-2xl font-semibold text-foreground mb-4">
                                              Βγάλε Λεφτά!
                                          </h3>
                                          <p className="text-muted-foreground">
                                              Λύνεις το πρόβλημα και παίρνεις τα λεφτά σου! Εμείς κρατάμε
                                              μικρή προμήθεια και εσύ απολαμβάνεις το υπόλοιπο. Όσο καλύτερος
                                              είσαι, τόσο περισσότερα κερδίζεις!
                                          </p>
                                      </div>
                                  </div>
                                  {/* Back */}
                                  <div
                                      className="absolute inset-0 h-full w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                                      <div
                                          className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-8 h-full flex flex-col justify-center text-white">
                                          <div className="text-center">
                                              <div className="text-5xl mb-4">💰</div>
                                              <h4 className="text-xl font-bold mb-3">Τι Κερδίζεις;</h4>
                                              <ul className="text-sm space-y-2 text-left">
                                                  <li className="flex items-start gap-2">
                                                      <Zap className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>70-85% από κάθε δουλειά</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <Zap className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Μηνιαία πληρωμή εγγυημένη</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <Zap className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Bonus για υψηλή βαθμολογία</span>
                                                  </li>
                                                  <li className="flex items-start gap-2">
                                                      <Zap className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                                      <span>Απεριόριστες δουλειές</span>
                                                  </li>
                                              </ul>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </section>

              {/* Section 3: Commission Structure for Fixers */}
              <section className="py-20 px-4">
                  <div className="max-w-6xl mx-auto">
                      <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
                          Πόσα Θα Βγάζεις;
                      </h2>
                      <p className="text-center text-muted-foreground mb-16 text-lg">
                          Όσο καλύτερος είσαι, τόσο περισσότερα κερδίζεις!
                      </p>

                      <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                          {/* Tier 1: Beginner */}
                          <div
                              className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg transition-all hover:-translate-y-1">
                              <div className="text-center mb-6">
                                  <h3 className="text-2xl font-bold text-foreground mb-2">Αρχάριος</h3>
                                  <p className="text-sm text-muted-foreground mb-4">Ξεκινάς τώρα</p>
                                  <div className="mb-4">
                                      <span className="text-4xl font-bold text-foreground">30%</span>
                                      <span className="text-muted-foreground"> για εσένα</span>
                                  </div>
                              </div>

                              <ul className="space-y-3 mb-8">
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">0-10 επιτυχημένες δουλειές</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Βασική υποστήριξη</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Μηνιαία πληρωμή</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Προφίλ σε όλη την πλατφόρμα</span>
                                  </li>
                              </ul>
                          </div>

                          {/* Tier 2: Professional */}
                          <div className="bg-card border-2 border-primary rounded-2xl p-8 shadow-xl relative scale-105">
                              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
                  🌟 ΔΗΜΟΦΙΛΕΣ
                </span>
                              </div>

                              <div className="text-center mb-6 mt-2">
                                  <h3 className="text-2xl font-bold text-foreground mb-2">Επαγγελματίας</h3>
                                  <p className="text-sm text-muted-foreground mb-4">Έμπειρος fixer</p>
                                  <div className="mb-4">
                                      <span className="text-5xl font-bold text-primary">60%</span>
                                      <span className="text-muted-foreground"> για εσένα</span>
                                  </div>
                              </div>

                              <ul className="space-y-3 mb-8">
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground font-medium">11-50 επιτυχημένες δουλειές</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground font-medium">Προτεραιότητα σε αιτήματα</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground font-medium">Πληρωμή κάθε 2 εβδομάδες</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground font-medium">Dedicated support</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground font-medium">Προβολή στην αρχική</span>
                                  </li>
                              </ul>
                          </div>

                          {/* Tier 3: Elite */}
                          <div
                              className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg transition-all hover:-translate-y-1">
                              <div className="text-center mb-6">
                                  <h3 className="text-2xl font-bold text-foreground mb-2">Elite Fixer</h3>
                                  <p className="text-sm text-muted-foreground mb-4">Ο καλύτερος</p>
                                  <div className="mb-4">
                                      <span className="text-4xl font-bold text-foreground">85%</span>
                                      <span className="text-muted-foreground"> για εσένα</span>
                                  </div>
                              </div>

                              <ul className="space-y-3 mb-8">
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">50+ επιτυχημένες δουλειές</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Πρώτη προτεραιότητα</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Εβδομαδιαία πληρωμή</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">VIP support</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Αποκλειστικές δουλειές</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-muted-foreground">Bonus για performance</span>
                                  </li>
                              </ul>
                          </div>
                      </div>

                      <div className="mt-12 text-center">
                          <p className="text-sm text-muted-foreground">
                              💳 Πληρωμές μέσω τράπεζας • Φορολογική ενημερότητα • 100% νόμιμα
                          </p>
                      </div>

                      <div className="mt-12 text-center">
                          <div
                              className="inline-block bg-gradient-to-r from-primary/10 to-purple-500/10 border-2 border-primary/30 rounded-2xl p-8 max-w-3xl">
                              <p className="text-xl md:text-2xl font-bold text-foreground mb-4">
                                  Ξεκίνα Να Βγάζεις Λεφτά Σήμερα!
                              </p>
                              <p className="text-lg text-muted-foreground mb-6">
                                  Οι γνωριμίες σου αξίζουν. Μην τις αφήνεις να πάνε χαμένες.
                                  Γίνε μέλος του μεγαλύτερου δικτύου fixers στην Ελλάδα!
                              </p>
                              <button
                                  onClick={() =>
                                      document
                                          .getElementById("waitlist")
                                          .scrollIntoView({behavior: "smooth"})
                                  }
                                  className="px-8 py-4 bg-primary text-primary-foreground rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
                              >
                                  ΕΓΓΡΑΦΗ ΤΩΡΑ - ΔΩΡΕΑΝ!
                              </button>
                          </div>
                      </div>
                  </div>
              </section>

              {/* Section 3.5: VIP Money Handling Techniques */}
              <section className="py-20 px-4">
                  <div className="max-w-6xl mx-auto">
                      <div className="text-center mb-12">
                          <div
                              className="inline-block bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full px-6 py-2 mb-4">
                              <span className="text-sm font-bold text-foreground">🔒 ΜΟΝΟ ΓΙΑ VIP ΜΕΛΗ</span>
                          </div>
                          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                              Πώς Διακινούμε τα Λεφτά σου;
                          </h2>
                          <p className="text-center text-muted-foreground text-lg max-w-3xl mx-auto">
                              Για τα Elite μέλη μας, προσφέρουμε προηγμένες τεχνικές διακίνησης
                              κεφαλαίων. 100% καθαρά, 100% ασφαλή, 0% προβλήματα!
                          </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
                          {/* Technique 1: Offshore Accounts */}
                          <div
                              className="bg-gradient-to-br from-card to-primary/5 border-2 border-primary/30 rounded-2xl p-8 hover:shadow-2xl transition-all hover:scale-105">
                              <div className="flex items-start gap-4 mb-6">
                                  <div
                                      className="w-16 h-16 bg-gradient-to-br from-primary to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                                      <span className="text-3xl">🏝️</span>
                                  </div>
                                  <div>
                                      <h3 className="text-2xl font-bold text-foreground mb-2">
                                          Offshore Λογαριασμοί
                                      </h3>
                                      <p className="text-sm text-primary font-semibold">
                                          Cayman Islands & Panama
                                      </p>
                                  </div>
                              </div>

                              <p className="text-muted-foreground mb-6">
                                  Ανοίγουμε για εσένα offshore λογαριασμούς στα Cayman Islands ή στον
                                  Παναμά. Τα λεφτά σου πάνε απευθείας εκεί, καθαρά και ασφαλή.
                                  Μηδενικοί φόροι, μηδενικά ερωτήματα!
                              </p>

                              <ul className="space-y-3">
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Πλήρης ανωνυμία:</strong> Κανείς δεν ξέρει τίποτα
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Μηδενική φορολογία:</strong> Κράτα ΟΛΑ τα κέρδη σου
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Ασφαλής διαχείριση:</strong> Εμείς φροντίζουμε τα πάντα
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Άμεση πρόσβαση:</strong> Τα λεφτά είναι δικά σου όποτε θες
                  </span>
                                  </li>
                              </ul>

                              <div className="mt-6 pt-6 border-t border-primary/20">
                                  <p className="text-xs text-muted-foreground italic">
                                      * Διαθέσιμο για Elite Fixers με 50+ επιτυχημένες συναλλαγές
                                  </p>
                              </div>
                          </div>

                          {/* Technique 2: Adoption Method */}
                          <div
                              className="bg-gradient-to-br from-card to-blue-500/5 border-2 border-blue-500/30 rounded-2xl p-8 hover:shadow-2xl transition-all hover:scale-105">
                              <div className="flex items-start gap-4 mb-6">
                                  <div
                                      className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                                      <span className="text-3xl">👨‍👦</span>
                                  </div>
                                  <div>
                                      <h3 className="text-2xl font-bold text-foreground mb-2">
                                          Μέθοδος Υιοθεσίας
                                      </h3>
                                      <p className="text-sm text-blue-500 font-semibold">
                                          100% Νόμιμη & Αναστρέψιμη
                                      </p>
                                  </div>
                              </div>

                              <p className="text-muted-foreground mb-6">
                                  Σε υιοθετούμε νομικά για μικρό χρονικό διάστημα! Τα λεφτά έρχονται
                                  ως "γονική παροχή" - εντελώς νόμιμα και αφορολόγητα. Μετά αναιρούμε
                                  την υιοθεσία, καθαρά και λεία. Μαγικό!
                              </p>

                              <ul className="space-y-3">
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>100% νόμιμη διαδικασία:</strong> Όλα μέσα στο νόμο
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Γονική παροχή:</strong> Αφορολόγητη μεταφορά χρημάτων
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Αναστρέψιμη:</strong> Ακύρωση υιοθεσίας σε 30 μέρες
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Καθαρό ιστορικό:</strong> Κανένα ίχνος μετά την ακύρωση
                  </span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm">
                                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"/>
                                      <span className="text-foreground">
                    <strong>Ομαλή διαδικασία:</strong> Εμείς χειριζόμαστε όλα τα χαρτιά
                  </span>
                                  </li>
                              </ul>

                              <div className="mt-6 pt-6 border-t border-blue-500/20">
                                  <p className="text-xs text-muted-foreground italic">
                                      * Διαθέσιμο για Elite Fixers με ποσά άνω των €10,000/μήνα
                                  </p>
                              </div>
                          </div>
                      </div>

                      {/* Info Box */}
                      <div className="max-w-4xl mx-auto">
                          <div
                              className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 border-2 border-yellow-500/30 rounded-2xl p-8">
                              <div className="flex items-start gap-4">
                                  <div
                                      className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-2xl">🔐</span>
                                  </div>
                                  <div>
                                      <h4 className="text-xl font-bold text-foreground mb-3">
                                          Απόλυτη Διακριτικότητα & Ασφάλεια
                                      </h4>
                                      <p className="text-muted-foreground mb-4">
                                          Οι τεχνικές μας είναι αποδεδειγμένες και δοκιμασμένες από χιλιάδες
                                          επιτυχημένους
                                          fixers. Έχουμε δικηγόρους, λογιστές, και νομικούς συμβούλους που φροντίζουν
                                          ώστε ΟΛΑ να είναι στο πλαίσιο του νόμου (ή τουλάχιστον να φαίνονται έτσι 😉).
                                      </p>
                                      <div className="grid md:grid-cols-3 gap-4 mt-6">
                                          <div className="flex items-center gap-2 text-sm">
                                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                              <span className="text-foreground font-medium">Μηδενικοί κίνδυνοι</span>
                                          </div>
                                          <div className="flex items-center gap-2 text-sm">
                                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                              <span className="text-foreground font-medium">Πλήρης προστασία</span>
                                          </div>
                                          <div className="flex items-center gap-2 text-sm">
                                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                              <span className="text-foreground font-medium">Εγγυημένη επιτυχία</span>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          </div>

                          <div className="mt-8 text-center">
                              <p className="text-sm text-muted-foreground">
                                  💼 Αυτές οι υπηρεσίες είναι διαθέσιμες ΜΟΝΟ για Elite Fixers •
                                  Επικοινωνήστε με το VIP Support για περισσότερες λεπτομέρειες
                              </p>
                          </div>
                      </div>
                  </div>
              </section>

              {/* Section 5: Why Join Us */}
              <section className="py-16 px-4">
                  <div className="max-w-6xl mx-auto">
                      <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
                          Γιατί Να Γίνεις Μέλος;
                      </h2>
                      <p className="text-center text-muted-foreground mb-16 text-lg">
                          Οι γνωριμίες σου αξίζουν. Μην τις αφήνεις να πάνε χαμένες.
                          Γίνε μέλος του μεγαλύτερου δικτύου fixers στην Ελλάδα!
                      </p>

                      <div className="grid md:grid-cols-2 gap-8">
                          {/* Benefit 1 */}
                          <div
                              className="bg-card border border-border rounded-xl p-8 hover:shadow-lg transition-shadow">
                              <div className="flex items-start gap-4">
                                  <div
                                      className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                      <Zap className="w-6 h-6 text-primary"/>
                                  </div>
                                  <div>
                                      <h3 className="text-xl font-semibold text-foreground mb-2">
                                          Ελευθερία Ωραρίου
                                      </h3>
                                      <p className="text-muted-foreground">
                                          Δουλεύεις όποτε θες, όσο θες. Καμία πίεση, καμία δέσμευση.
                                          Εσύ αποφασίζεις πόσο χρόνο θα αφιερώσεις.
                                      </p>
                                  </div>
                              </div>
                          </div>

                          {/* Benefit 2 */}
                          <div
                              className="bg-card border border-border rounded-xl p-8 hover:shadow-lg transition-shadow">
                              <div className="flex items-start gap-4">
                                  <div
                                      className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                      <CheckCircle2 className="w-6 h-6 text-primary"/>
                                  </div>
                                  <div>
                                      <h3 className="text-xl font-semibold text-foreground mb-2">
                                          Εγγυημένη Ασφάλεια
                                      </h3>
                                      <p className="text-muted-foreground">
                                          Εμείς σε προστατεύουμε. Επαληθευμένοι πελάτες, ασφαλείς πληρωμές,
                                          και υποστήριξη σε κάθε βήμα.
                                      </p>
                                  </div>
                              </div>
                          </div>

                          {/* Benefit 3 */}
                          <div
                              className="bg-card border border-border rounded-xl p-8 hover:shadow-lg transition-shadow">
                              <div className="flex items-start gap-4">
                                  <div
                                      className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                      <Users className="w-6 h-6 text-primary"/>
                                  </div>
                                  <div>
                                      <h3 className="text-xl font-semibold text-foreground mb-2">
                                          Μεγάλο Δίκτυο Πελατών
                                      </h3>
                                      <p className="text-muted-foreground">
                                          Χιλιάδες πελάτες ψάχνουν για βοήθεια κάθε μέρα. Δεν χρειάζεται
                                          να ψάχνεις - εμείς φέρνουμε τις δουλειές σε εσένα!
                                      </p>
                                  </div>
                              </div>
                          </div>

                          {/* Benefit 4 */}
                          <div
                              className="bg-card border border-border rounded-xl p-8 hover:shadow-lg transition-shadow">
                              <div className="flex items-start gap-4">
                                  <div
                                      className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                      <Trophy className="w-6 h-6 text-primary"/>
                                  </div>
                                  <div>
                                      <h3 className="text-xl font-semibold text-foreground mb-2">
                                          Αναγνώριση & Επιβράβευση
                                      </h3>
                                      <p className="text-muted-foreground">
                                          Όσο καλύτερος γίνεσαι, τόσο περισσότερα κερδίζεις. Bonus,
                                          προτεραιότητα, και αποκλειστικές ευκαιρίες!
                                      </p>
                                  </div>
                              </div>
                          </div>
                      </div>

                  </div>
              </section>

              {/* Section 6: Waitlist Signup for Fixers */}
              <section id="waitlist" className="py-20 px-4 bg-muted/30">
                  <div className="max-w-2xl mx-auto text-center">
                      <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                          Μπες στη Λίστα των Fixers!
                      </h2>
                      <p className="text-muted-foreground mb-8 text-lg">
                          Κάνε εγγραφή τώρα και ξεκίνα να βγάζεις λεφτά από τις γνωριμίες σου!
                          100% δωρεάν - χωρίς κρυφές χρεώσεις!
                      </p>

                      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                          <div className="flex flex-col sm:flex-row gap-4">
                              <input
                                  type="email"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  placeholder="Βάλε το email σου εδώ"
                                  required
                                  className="flex-1 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                              />
                              <button
                                  type="submit"
                                  disabled={submitted}
                                  className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                  {submitted ? "✓ Μέσα!" : "ΓΙΝΕ FIXER!"}
                              </button>
                          </div>
                      </form>

                      {submitted && (
                          <p className="mt-4 text-green-600 dark:text-green-400 font-medium">
                              ΜΠΡΑΒΟ! Θα επικοινωνήσουμε σύντομα για την εγγραφή σου!
                          </p>
                      )}

                      <p className="mt-6 text-sm text-muted-foreground">
                          Ήδη <span className="font-semibold text-foreground">{fixersCount}</span>{" "}
                          fixers περιμένουν να ξεκινήσουν!
                      </p>

                      <div className="mt-8 flex justify-center gap-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary"/>
              100% δωρεάν εγγραφή
            </span>
                          <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary"/>
              Χωρίς δεσμεύσεις
            </span>
                      </div>
                  </div>
              </section>

              {/* Footer */}
              <footer className="py-8 px-4 border-t border-border">
                  <div className="max-w-6xl mx-auto text-center text-muted-foreground text-sm">
                      <p>&copy; 2025 Volepse.me. Με επιφύλαξη παντός δικαιώματος.</p>
                  </div>
              </footer>
          </>
        )}
      </div>

      {/* Modal - Shared between both views */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300"
          onClick={(e) => {
            // Close modal only if clicking on the backdrop (not the modal content)
            if (e.target === e.currentTarget) {
              setShowModal(false);
              trackModal('close');
            }
          }}
        >
          <div className="bg-card border-2 border-primary rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in duration-300">
            {/* Close button */}
            <button
              onClick={() => {
                setShowModal(false);
                trackModal('close');
              }}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Modal content */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mailbox className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Περίμενε λίγο!
              </h3>
              <p className="text-lg text-muted-foreground mb-2">
                Μην Φύγεις Χωρίς το Βύσμα σου...
              </p>
              <p className="text-sm text-muted-foreground">
                Γίνε τωρα <span className="text-primary font-semibold">μέλος</span> και
                πάρε <span className="text-primary font-semibold">50% έκπτωση</span> στην πρώτη σου σύνδεση.
              </p>
            </div>

            {!modalSubmitted ? (
              <form onSubmit={handleModalSubmit} className="space-y-4">
                <input
                  type="email"
                  value={modalEmail}
                  onChange={(e) => setModalEmail(e.target.value)}
                  placeholder="Βάλε το email σου εδώ"
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
                >
                  ΚΛΕΙΣΕ ΜΟΥ ΘΕΣΗ! 🚀
                </button>
              </form>
            ) : (
              <div className="text-center py-4">
                <div className="text-6xl mb-4">✓</div>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                  Μέσα! 🎉
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Θα σε καλέσουμε σύντομα με τη μεγάλη έκπτωση!
                </p>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-primary" />
                Χωρίς spam
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-primary" />
                100% ασφαλές
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsentBanner />
    </div>
  );
};

export default LandingPage;
